import './App.css';
import { useMemo, useState } from 'react';
import { Link, Route, Routes } from 'react-router-dom';
import Legal from './Legal';

function verdictLabel(verdict) {
  if (verdict === 'Safe') return 'בטוח';
  if (verdict === 'Suspicious') return 'חשוד';
  return 'כנראה פישינג';
}

function verdictTone(verdict) {
  if (verdict === 'Safe') return 'good';
  if (verdict === 'Suspicious') return 'warn';
  return 'bad';
}

function riskLevelFromScore(score) {
  const s = Number(score || 0);
  if (s >= 80) return 5;
  if (s >= 60) return 4;
  if (s >= 40) return 3;
  if (s >= 25) return 2;
  return 1;
}

function HighlightedText({ text, excerpts }) {
  const parts = useMemo(() => {
    const cleanText = String(text || '');
    const needles = (excerpts || [])
      .map((e) => (e ? String(e) : ''))
      .filter(Boolean)
      .filter((e) => e.length >= 3)
      .slice(0, 10);

    if (!cleanText || needles.length === 0) return [{ t: cleanText, m: false }];

    // Highlight first match by longest excerpt.
    const sorted = [...needles].sort((a, b) => b.length - a.length);
    const needle = sorted.find((n) => cleanText.toLowerCase().includes(n.toLowerCase()));
    if (!needle) return [{ t: cleanText, m: false }];

    const lower = cleanText.toLowerCase();
    const idx = lower.indexOf(needle.toLowerCase());
    if (idx < 0) return [{ t: cleanText, m: false }];

    return [
      { t: cleanText.slice(0, idx), m: false },
      { t: cleanText.slice(idx, idx + needle.length), m: true },
      { t: cleanText.slice(idx + needle.length), m: false },
    ];
  }, [text, excerpts]);

  return (
    <span>
      {parts.map((p, i) =>
        p.m ? (
          <mark key={i} className="Mark">
            {p.t}
          </mark>
        ) : (
          <span key={i}>{p.t}</span>
        )
      )}
    </span>
  );
}

function Home() {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [consent, setConsent] = useState(false);

  const canSubmit = url.trim().length > 0 && !loading && consent;

  async function onAnalyze(e) {
    e.preventDefault();
    setError('');
    setResult(null);
    setLoading(true);
    try {
      const res = await fetch('/api/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      });
      const isJson = (res.headers.get('content-type') || '').includes('application/json');
      const data = isJson ? await res.json().catch(() => ({})) : {};
      if (!res.ok) {
        const hint =
          res.status === 404
            ? 'Local dev tip: run `npm run dev` (Vercel Dev) so `/api/analyze` exists locally.'
            : '';
        throw new Error(data.error || `Request failed (HTTP ${res.status}). ${hint}`.trim());
      }
      setResult(data);
    } catch (err) {
      const msg = err && err.message ? err.message : 'Unexpected error';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  const overall = result && result.overall;
  const findings = (overall && overall.findings) || [];
  const excerpts = findings.map((f) => f && f.excerpt).filter(Boolean);
  const riskLevel = overall ? riskLevelFromScore(overall.score) : 1;

  return (
    <div className="App">
      <div className="Shell">
        <header className="Header">
          <div className="Brand">
            <div className="BrandTitle">PhishNLearn</div>
            <div className="BrandSubtitle">
              כלי לבדיקת לינקים - האם הלינק שקיבלת הוא פישינג? הכניסו ותדעו!
            </div>
          </div>
        </header>

        <main className="Main">
          <form className="Card" onSubmit={onAnalyze} style={{ maxWidth: 820, margin: '0 auto', width: '100%' }}>
            <label className="Label" htmlFor="url">
              הדביקו כאן קישור לבדיקה
            </label>
            <div className="Row">
              <input
                id="url"
                className="Input"
                type="text"
                inputMode="url"
                autoComplete="off"
                placeholder="לדוגמה: https://example.com"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
              />
              <button className="Button" type="submit" disabled={!canSubmit}>
                {loading ? 'מנתח…' : 'נתח'}
              </button>
            </div>

            <div className="Hint">
              הטיפ: אם זה קישור מקוצר, נבדוק גם את המקצר וגם את היעד לאחר ההפניה.
            </div>

            <div style={{ marginTop: 10, fontSize: 12, color: 'rgba(232, 238, 252, 0.72)' }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                />
                <span>
                  אני מאשר/ת שהבנתי שהניתוח הוא למטרות לימודיות בלבד, ושהשימוש בתוצאה הוא באחריותי
                  הבלעדית.{' '}
                  <Link className="Link" to="/legal">
                    לקריאת כתב הוויתור המלא
                  </Link>
                  .
                </span>
              </label>
            </div>

            {error ? <div className="Alert AlertBad">{error}</div> : null}
          </form>

          {overall ? (
            <section className="Card" style={{ maxWidth: 820, margin: '0 auto', width: '100%' }}>
              <div className="ResultTop">
                <div className={`Badge Badge--${verdictTone(overall.verdict)}`}>
                  {verdictLabel(overall.verdict)}
                </div>
                <div className="RiskScale" aria-label="Risk scale">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <span
                      key={n}
                      className={`RiskStep RiskStep--${n} ${n <= riskLevel ? 'RiskStep--on' : ''}`}
                      title={
                        n === 1
                          ? 'ירוק'
                          : n === 2
                          ? 'ירוק-צהוב'
                          : n === 3
                          ? 'צהוב'
                          : n === 4
                          ? 'צהוב-אדום'
                          : 'אדום'
                      }
                    />
                  ))}
                </div>
              </div>

              <div className="UrlPreview">
                <div className="UrlTitle">הקישור שנבדק</div>
                <div className="UrlValue">
                  <HighlightedText text={result.expansion?.finalUrl || result.normalizedUrl} excerpts={excerpts} />
                </div>
              </div>

              <div className="Findings">
                <div className="SectionTitle">ממצאים</div>
                {findings.length === 0 ? (
                  <div className="Muted">אין ממצאים.</div>
                ) : (
                  <ul className="List">
                    {findings.slice(0, 12).map((f, idx) => (
                      <li key={idx} className={`ListItem Sev Sev--${f.severity || 'info'}`}>
                        <div className="ListTitle">{f.title}</div>
                        <div className="ListReason">{f.reason}</div>
                        {f.excerpt ? (
                          <div className="ListExcerpt">
                            <span className="ExcerptLabel">חלק חשוד:</span>{' '}
                            <code className="Code">{f.excerpt}</code>
                          </div>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="Explanation">
                <div className="SectionTitle">הסבר</div>
                <pre className="Pre">{overall.explanation}</pre>
              </div>

              {result.expansion ? (
                <details className="Details">
                  <summary>פרטי הפניות (Redirects)</summary>
                  <div className="DetailsInner">
                    <div className="Muted">
                      שיטה: {result.expansion.methodTried || 'HEAD'}{' '}
                      {result.expansion.ok ? '' : '(לא אומת ברשת)'}
                    </div>
                    <ol className="Chain">
                      {(result.expansion.chain || []).map((h, i) => (
                        <li key={i} className="ChainItem">
                          <div className="ChainUrl">
                            <code className="Code">{h.url}</code>
                          </div>
                          <div className="ChainMeta">
                            {h.status ? `סטטוס ${h.status}` : 'סטטוס לא ידוע'}
                            {h.location ? (
                              <>
                                {' '}
                                → <code className="Code">{h.location}</code>
                              </>
                            ) : null}
                          </div>
                        </li>
                      ))}
                    </ol>
                  </div>
                </details>
              ) : null}
            </section>
          ) : null}

          <footer className="Footer">
            <div className="FooterText">
              זהו כלי חינוכי. תמיד ודאו את הדומיין ידנית ואל תזינו סיסמאות דרך קישורים חשודים.
            </div>
            <div style={{ marginTop: 8 }}>
              <Link className="Link" to="/legal">
                הצהרה משפטית / כתב ויתור
              </Link>
            </div>
          </footer>
        </main>
      </div>
    </div>
  );
}

function App() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/legal" element={<Legal />} />
    </Routes>
  );
}

export default App;
